import React, { Component } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { SubmissionError } from "redux-form";
import { get, put, remove } from "./sessionStorage";
import { jwtDecode } from "jwt-decode";
import { head, isNil, isEmpty } from "lodash";
import {
  setSidebarItems,
  breadcrumbs,
  setLoading,
  setAuth,
  initMenu,
} from "../actions/appActions";
import { addCacheControlHeader, addCorrelationIdHeader } from "../utils/fetch";
import { isInternal } from "../agenda/Cro/CroUtils";
import Bugsnag from "./bugsnag";

const user = "user";
const sessionStartUrl = "sessionStartUrl";

const DEV_BYPASS_AUTH = false;
const DEV_USER = {
  sub: "dev-user",
  name: "Dev User",
  aut: [
    "ROLE_INTERNAL",
    "ROLE_INTERNAL_ADMIN",
    "ROLE_INTERNAL_USER",
    "ROLE_INTERNAL_OFFICIAL_ADMIN",
    "ROLE_INTERNAL_JUDGE",
    "ROLE_OVM",
    "ROLE_LEGITIMATE",
    "ROLE_OFFICIAL",
    "ROLE_PUBLIC",
    "stret users",
  ],
};

const getUser = () => (get(user) && get(user) !== "null" ? get(user) : null);
const setUser = (token) => put(user, token);
const removeUser = () => { remove(user); remove(sessionStartUrl); };
export const getDecodedUser = () =>
  DEV_BYPASS_AUTH ? DEV_USER : getUser() ? jwtDecode(getUser()) : {};

const setUserForBugsnag = () => {
  const user = getDecodedUser();
  Bugsnag.user = { id: user.sub, name: user.name };
};

/**
 * Zistí správnu startUrl pre prihláseného usera podľa jeho rolí v JWT.
 * Vracia URL kam má byť user presmerovaný keď pristúpi na stránku bez oprávnenia.
 */
export const getStartUrlForUser = (decodedUser) => {
  const storedUrl = get(sessionStartUrl);
  if (storedUrl) return storedUrl;

  if (!decodedUser || !Array.isArray(decodedUser.aut)) return "/";

  const roles = decodedUser.aut;

  if (roles.includes("ROLE_INTERNAL")) {
    if (roles.includes("ROLE_INTERNAL_OFFICIAL_ADMIN"))
      return "/administrator/funkcionari";
    if (roles.includes("ROLE_INTERNAL_JUDGE")) return "/soudce/funkcionari";
    return "/spravce/podpurne-organy";
  }
  if (roles.includes("ROLE_OVM")) return "/podpurny-organ/funkcionari";
  if (roles.includes("ROLE_LEGITIMATE")) return "/opravneny-organ/funkcionari";
  if (roles.includes("ROLE_OFFICIAL")) return "/funkcionar/oznameni";
  if (roles.includes("ROLE_PUBLIC")) return "/verejnost-prihlasen/hlavni";

  return "/";
};

export const addBearerHeader = (headers) => {
  const token = getUser();

  if (token === null) {
    return headers;
  }

  const authHead = { Authorization: `Bearer ${token}` };
  if (headers instanceof Headers) {
    headers.append("Authorization", authHead.Authorization);
    return headers;
  }
  return new Headers(authHead);
};

export const kerberosLoginAction = (url, nextUrl) => () => async (dispatch) => {
  dispatch(setLoading(true, "Přihlašování..."));
  const response = await fetch(`${url}?_=${Date.now()}`, {
    method: "GET",
    headers: addCacheControlHeader(addCorrelationIdHeader()),
    credentials: "same-origin", // due to kerberos login
  });
  if (response.ok) {
    const token = response.headers.get("Bearer");
    if (isNil(token)) {
      Bugsnag.notify(
        "Wrong Kerberos login - token is null",
        `token: ${token}, response.url: ${response.url}, response.status: ${response.status}`,
      );
      dispatch(setLoading());
      throw new SubmissionError({
        _error: "Chyba přihlášení - neplatný token",
      });
    }

    let decodedToken;
    try {
      decodedToken = jwtDecode(token);
    } catch (e) {
      Bugsnag.notify("Wrong Kerberos login - token decode failed", e);
      dispatch(setLoading());
      throw new SubmissionError({
        _error: "Chyba přihlášení - neplatný token",
      });
    }

    if (!hasExpectedRoleForStartUrl(decodedToken, nextUrl)) {
      Bugsnag.notify(
        "Wrong Kerberos login - role mismatch for login endpoint",
        `nextUrl: ${nextUrl}, aut: ${decodedToken?.aut}`,
      );
      removeUser();
      dispatch(setLoading());
      throw new SubmissionError({
        _error: "Chyba přihlášení - špatné jméno nebo heslo.",
      });
    }

    setUser(token);
    put(sessionStartUrl, nextUrl);
    setUserForBugsnag();
    dispatch(setLoading());
    dispatch(push(nextUrl));
  } else {
    dispatch(setLoading());
    throw new SubmissionError({ _error: "Nelze se přihlásit" });
  }
};

const hasExpectedRoleForStartUrl = (decodedUser, startUrl) => {
  if (!decodedUser || !Array.isArray(decodedUser.aut)) return false;
  const roles = decodedUser.aut;
  if (!startUrl || startUrl === "/") return true;
  if (startUrl.startsWith("/administrator"))
    return (
      roles.includes("ROLE_INTERNAL") &&
      roles.includes("ROLE_INTERNAL_OFFICIAL_ADMIN")
    );
  if (startUrl.startsWith("/spravce"))
    return (
      roles.includes("ROLE_INTERNAL") &&
      (roles.includes("ROLE_INTERNAL_ADMIN") ||
        roles.includes("ROLE_INTERNAL_USER"))
    );
  if (startUrl.startsWith("/soudce"))
    return (
      roles.includes("ROLE_INTERNAL") && roles.includes("ROLE_INTERNAL_JUDGE")
    );
  if (startUrl.startsWith("/podpurny-organ")) return roles.includes("ROLE_OVM");
  if (startUrl.startsWith("/opravneny-organ"))
    return roles.includes("ROLE_LEGITIMATE");
  if (startUrl.startsWith("/funkcionar"))
    return roles.includes("ROLE_OFFICIAL");
  if (startUrl.startsWith("/verejnost-prihlasen"))
    return roles.includes("ROLE_PUBLIC");
  return true;
};

export const loginAction =
  (url, nextUrl) =>
  ({ username, password }) =>
  async (dispatch) => {
    const loginToken = btoa(
      unescape(encodeURIComponent(`${username}:${password}`)),
    );
    dispatch(setLoading(true, "Přihlašování..."));
    const response = await fetch(url, {
      method: "GET",
      headers: addCacheControlHeader(
        addCorrelationIdHeader(
          new Headers({ Authorization: `Basic ${loginToken}` }),
        ),
      ),
    });
    if (response.ok) {
      const token = response.headers.get("Bearer");
      if (isNil(token)) {
        Bugsnag.notify(
          "Wrong login - token is null",
          `token: ${token}, response.url: ${response.url}, response.status: ${response.status}`,
        );
        dispatch(setLoading());
        throw new SubmissionError({
          _error: "Chyba přihlášení - špatné jméno nebo heslo",
        });
      }

      let decodedToken;
      try {
        decodedToken = jwtDecode(token);
      } catch (e) {
        Bugsnag.notify("Wrong login - token decode failed", e);
        dispatch(setLoading());
        throw new SubmissionError({
          _error: "Chyba přihlášení - neplatný token",
        });
      }

      if (!hasExpectedRoleForStartUrl(decodedToken, nextUrl)) {
        Bugsnag.notify(
          "Wrong login - role mismatch for login endpoint",
          `nextUrl: ${nextUrl}, aut: ${decodedToken?.aut}`,
        );
        removeUser();
        dispatch(setLoading());
        throw new SubmissionError({
          _error: "Chyba přihlášení - špatné jméno nebo heslo.",
        });
      }

      setUser(token);
      put(sessionStartUrl, nextUrl);
      setUserForBugsnag();
      dispatch(setLoading());
      dispatch(push(nextUrl));
    } else {
      Bugsnag.notify(
        "Wrong login - response is nok",
        `response.url: ${response.url}, response.status: ${response.status}`,
      );
      dispatch(setLoading());
      throw new SubmissionError({
        _error: `Pokus o přihlášení byl neúspěšný (${response.status})`,
      });
    }
  };

export const jipLoginAction = (sessionId) => async (dispatch) => {
  dispatch(setLoading(true, "Přihlašování..."));
  const response = await fetch(
    `/podpurny-organ/api/auth/jip?sessionId=${sessionId}`,
    {
      method: "GET",
      params: { sessionId: sessionId },
    },
  );
  if (response.ok) {
    const token = response.headers.get("Bearer");
    if (isNil(token)) {
      Bugsnag.notify(
        "Wrong JIP login - token is null",
        `token: ${token}, response.url: ${response.url}, response.status: ${response.status}`,
      );
      dispatch(setLoading());
      throw new SubmissionError({
        _error: "Chyba přihlášení - špatné jméno nebo heslo",
      });
    }
    setUser(token);
    setUserForBugsnag();
    dispatch(setLoading());
    dispatch(push("/podpurny-organ/funkcionari"));
  } else {
    Bugsnag.notify(
      "Wrong JIP login - response is nok",
      `response.url: ${response.url}, response.status: ${response.status}`,
    );
    dispatch(setLoading());
    throw new SubmissionError({
      _error: `Pokus o přihlášení byl neúspěšný (${response.status})`,
    });
  }
};

export const samlLoginAction = (samlToken, user) => async (dispatch) => {
  dispatch(setLoading(true, "Přihlašování..."));

  if (isNil(samlToken) || samlToken === "") {
    Bugsnag.notify("Wrong NIA login - token is null", `token: ${samlToken}`);
    console.log("token missing");
    dispatch(setLoading());
    throw new SubmissionError({
      _error: "Uživatel nebyl nalezen v systému CRO",
    });
  } else if (samlToken === "error") {
    dispatch(setLoading());
    throw new SubmissionError({ _error: "Pokus o přihlášení byl neúspěšný" });
  } else {
    setUser(samlToken);
    setUserForBugsnag();
    dispatch(setLoading());
    if (user === "official") {
      dispatch(push("/funkcionar/oznameni"));
    } else if (user === "public") {
      dispatch(push("/verejnost-prihlasen/hlavni"));
    }
  }
  // const response = await fetch(`/funkcionar/api/auth/jip?sessionId=${sessionId}`, {
  //   method: 'GET',
  //   params: { sessionId: sessionId },
  // });
  // if (response.ok) {
  //   const token = response.headers.get('Bearer');
  //   if (isNil(token)) {
  //     Bugsnag.notify('Wrong JIP login - token is null', `token: ${token}, response.url: ${response.url}, response.status: ${response.status}`);
  //     dispatch(setLoading());
  //     throw new SubmissionError({ _error: 'Chyba přihlášení - špatné jméno nebo heslo' });
  //   }
  //   setUser(token);
  //   setUserForBugsnag();
  //   dispatch(setLoading());
  //   dispatch(push('/podpurny-organ/funkcionari'));
  // } else {
  //   Bugsnag.notify('Wrong JIP login - response is nok', `response.url: ${response.url}, response.status: ${response.status}`);
  //   dispatch(setLoading());
  //   throw new SubmissionError({ _error: `Pokus o přihlášení byl neúspěšný (${response.status})` });
  // }
};

// TODO SAML logout
// export const logoutAction = push => async () => {
//
//   Bugsnag.user = null;
//   const user = getUser();
//   // fetch with axios
//
//   // window.location.href = 'localhost:8080/api/saml/logout'
//
//   // we will call GET request to ``/saml/api/logout`` endpoint which will return status 302 and redirect url
//   // in case something bad happend and cors will not allow us to redirect, we will use window.location.href to
//   // set redirect url returned by given endpoint
//
//   axios.get(`/saml/api/logout`)
//     .then(response => {
//       console.log("sadasdad");
//       console.log(response.data.url);
//         // window.location.href = response.data.url;
//     })

// axios.get(`/saml/api/logout`)
//     .then(response => {
//       window.location.replace(response.data.url);
//     })
//     .catch(error => {
//       console.error("Error during logout:", error);
//     });

// try {
//
//   const response = await fetch('/saml/api/logout', {
//     method: 'GET',
//     headers: {
//       'Authorization': `Bearer ${user}`
//     }
//   });
//
//   if (response.ok) {
//     console.log('Logout successful');
//     // Additional logic if needed
//   } else {
//     console.error('Logout failed');
//   }
// } catch (error) {
//   console.error('Error during logout', error);
// } finally {
//   removeUser();
// }
// window.location.href = '/saml/api/logout';
// push('/saml/api/logout');
// }
const forceLogout = () => {
  removeUser();
  Bugsnag.user = null;
  window.location.href = "/?message=logout";
};

export const logoutAction = (push) => async () => {
  const token = getUser();

  if (token) {
    try {
      const rolePrefix = (getStartUrlForUser(getDecodedUser()) || "/").split("/")[1];
      if (rolePrefix) {
        await fetch(`/${rolePrefix}/api/audit-history/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      console.warn("Logout audit failed", e);
    }
  }

  removeUser();
  Bugsnag.user = null;

  try {
    if (token) {
      const response = await fetch("/saml/api/logout", {
        method: "GET",
        redirect: "manual",
        headers: { Authorization: `Bearer ${token}` },
      });
      const location = response.headers.get("Location");
      if (location) {
        window.location.href = location;
        return;
      }
    }
  } catch (e) {
    console.warn("Logout request failed", e);
  }

  window.location.href = "/?message=logout";
};

const refreshTokenAction = async () => {
  const user = getUser();
  if (user === null) return;
  const response = await fetch("/api/keepalive", {
    method: "GET",
    headers: addCacheControlHeader(
      addCorrelationIdHeader(new Headers({ Authorization: `Bearer ${user}` })),
    ),
  });
  if (response.ok) {
    const token = response.headers.get("Bearer");
    if (!isNil(token)) setUser(token);
  } else if (response.status !== 200) {
    forceLogout();
  }
};

export const auth = (authObj, AuthenticatedComponent) => {
  return connect((state) => ({
    item: head(state.sidebar.items),
    menuInitialized: Object.keys(state.sidebar.menu).length > 0,
  }))(
    class AuthComponet extends Component {
      componentWillMount() {
        this.authPassed = false;

        // Inicializuj menu ak ešte nie je (lazy import aby sa zabránilo circular dependency)
        if (!this.props.menuInitialized) {
          import("../agenda/Cro/routes").then((rsModule) => {
            if (!this.authPassed) return;
            const rs = rsModule.default;
            const { fromPairs } = require("lodash");
            this.props.dispatch(
              initMenu(
                fromPairs(Object.keys(rs).map((key) => [key, rs[key].menu])),
              ),
            );
            // Po inicializácii menu nastavíme sidebar items
            this.setUpMenu();
          });
        }

        const authResult = this.checkAuth();
        if (authResult === true) {
          this.authPassed = true;
          this.setRefreshInterval();
          this.setUpMenu();
        } else if (authResult === "no_token") {
          // Neprihlásený → na login
          removeUser();
          this.props.dispatch(push("/"));
        } else {
          // Prihlásený ale nemá oprávnenie → presmerovať na jeho startUrl
          const decodedUser = getDecodedUser();
          const correctUrl = getStartUrlForUser(decodedUser);
          this.props.dispatch(push(correctUrl));
        }
      }

      componentWillUnmount() {
        this.clearRefreshInterval();
      }

      setRefreshInterval = () => {
        refreshTokenAction();
        this.id = setInterval(refreshTokenAction, 60 * 1000);
      };

      clearRefreshInterval = () => {
        if (!isNil(this.id)) clearInterval(this.id);
      };

      setUpMenu = () => {
        const { dispatch } = this.props;
        // V DEV režime vždy nastavíme menu podľa authObj.name
        if (DEV_BYPASS_AUTH) {
          dispatch(setSidebarItems(authObj.name));
          return;
        }
        if (isInternal(authObj)) {
          if (getDecodedUser().aut.includes("stret users")) {
            dispatch(setSidebarItems("internal"));
          } else {
            dispatch(setSidebarItems(authObj.name));
          }
        } else {
          dispatch(setSidebarItems(authObj.name));
        }
      };

      checkAuth = () => {
        if (DEV_BYPASS_AUTH) {
          return true;
        }
        const user = getUser();
        const jwtCookie = document.cookie
          .split(";")
          .find((c) => c.trim().startsWith("jwt="));
        if (user === null && jwtCookie) {
          const jwt = jwtCookie.split("=")[1];
          console.log("JWT", jwt);
        }
        if (user === null) {
          Bugsnag.notify("Login error - user is null", "user: " + user);
          return "no_token";
        }

        const decodedUser = getDecodedUser();
        if (!decodedUser.aut.includes(authObj.role)) {
          Bugsnag.notify(
            "Login error - wrong role",
            "decodedUser.aut: " +
              decodedUser.aut +
              " authObj.role: " +
              authObj.role,
          );
          return "wrong_role";
        } else if (isInternal(authObj)) {
          const hasMore = isEmpty(authObj.more)
            ? true
            : authObj.more
                .map((str) => decodedUser.aut.includes(str))
                .reduce((prev, curr) => prev || curr, false);
          if (!hasMore) return "wrong_role";

          const storedUrl = get(sessionStartUrl);
          if (storedUrl && authObj.startUrl && !storedUrl.startsWith(authObj.startUrl)) {
            return "wrong_role";
          }

          return true;
        }

        return true;
      };

      render() {
        this.props.dispatch(setAuth(authObj));
        return getUser() !== null || DEV_BYPASS_AUTH ? (
          <AuthenticatedComponent {...this.props} auth={authObj} />
        ) : null;
      }
    },
  );
};

export const noauth = (NoAuthComponent) => {
  return connect((state) => ({
    menuInitialized: Object.keys(state.sidebar.menu || {}).length > 0,
  }))(
    class NoAuthComponet extends Component {
      componentWillMount() {
        // V DEV režime presmerujeme na /spravce
        if (DEV_BYPASS_AUTH && window.location.pathname === "/") {
          this.props.dispatch(push("/spravce/podpurne-organy"));
          return;
        }

        // Ak je user prihlásený, presmeruj na jeho startUrl
        const decodedUser = getDecodedUser();
        if (
          decodedUser &&
          Array.isArray(decodedUser.aut) &&
          decodedUser.aut.length > 0
        ) {
          const correctUrl = getStartUrlForUser(decodedUser);
          if (correctUrl && correctUrl !== "/") {
            this.props.dispatch(push(correctUrl));
            return;
          }
        }

        // Ensure menu is initialized (same lazy approach as in `auth`)
        if (!this.props.menuInitialized) {
          import("../agenda/Cro/routes").then((rsModule) => {
            const rs = rsModule.default;
            const { fromPairs } = require("lodash");
            this.props.dispatch(
              initMenu(
                fromPairs(Object.keys(rs).map((key) => [key, rs[key].menu])),
              ),
            );
            this.setUpMenu();
          });
        } else {
          this.setUpMenu();
        }

        this.props.dispatch(breadcrumbs([]));
      }

      setUpMenu = () => {
        const disableSpravceLogin = [
          "crotst.justice.cz",
          "cro.justice.cz",
          "cro.gov.cz",
        ].includes(window.location.hostname);
        const menu = disableSpravceLogin ? "publicFreeNoAdmin" : "publicFree";

        this.props.dispatch(setSidebarItems(menu));
      };

      render() {
        // V DEV režime na hlavnej stránke neukazujeme nič (presmerováva sa)
        if (DEV_BYPASS_AUTH && window.location.pathname === "/") {
          return null;
        }

        this.props.dispatch(setAuth({ startUrl: "/verejnost" }));
        return (
          <NoAuthComponent {...this.props} auth={{ startUrl: "/verejnost" }} />
        );
      }
    },
  );
};
